import { onUnmounted, ref, watch } from 'vue';
import { SCChannel } from 'sc-channel';
import { AxiosPromise, AxiosResponse } from '@libs/http';
import { useQueryClient } from '@tanstack/vue-query';

import { useSocket } from './useSocket';
import { UseQueryConfigV2, useQueryV2 } from './useQueryV2';

export type UseLiveQueryConfigV2<T> = UseQueryConfigV2<T> & {
  fetcher: (...arg: any[]) => AxiosPromise<T>;
  channel?: string;
  unsubscribe?: boolean | ((channelName: string) => boolean);
  getChannels?: (data: AxiosResponse<T> | null) => Array<string> | void;
  onUpdate: (signal: { code: number; data: object }) => void;
};

export function useLiveQueryV2<T>({
  fetcher,
  onUpdate,
  channel,
  unsubscribe = true,
  getChannels,
  ...options
}: UseLiveQueryConfigV2<T>): ReturnType<typeof useQueryV2<T>> {
  const channelsRef = ref<Record<string, SCChannel>>({});
  const { socket, onReady, connected } = useSocket();

  const queryClient = useQueryClient();

  const query = useQueryV2<T>(fetcher, {
    ...options,
    onSuccess: (result) => {
      options.onSuccess?.(result);

      onReady(() => {
        const channels = channel ? [channel] : getChannels?.(result);

        if (!channels) {
          return;
        }

        for (const name of channels) {
          if (socket.value && !channelsRef.value[name]) {
            channelsRef.value[name] = socket.value.subscribe(name, {
              batch: true,
            });

            if (typeof onUpdate === 'function') {
              channelsRef.value[name].watch(onUpdate);
            }
          }
        }
      });
    },
  });

  function handleUnsubscribe() {
    for (const [channelName, channel] of Object.entries(channelsRef.value)) {
      const isUnsubscribed =
        typeof unsubscribe === 'function'
          ? unsubscribe(channelName)
          : unsubscribe;

      if (isUnsubscribed) {
        channel.unsubscribe();
      }

      delete channelsRef.value[channelName];
      channel.unwatch(onUpdate);
    }
  }

  query.onKeyUpdated(handleUnsubscribe);
  onUnmounted(handleUnsubscribe);

  watch(connected, () => {
    if (!connected.value && !query.isRefetching.value) {
      queryClient.invalidateQueries(options.key);
    }
  });

  return { ...query };
}
